/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Money Transfer Component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.2.0
 */

import { FC } from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EBasketType } from '../../../modules/app/appConstants';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../../modules/internationalization/constants';
import { makeSelectMoneyTransferTranslation } from '../../../modules/internationalization/intSelectors';
import { IMoneyTransferTranslation } from '../../../modules/internationalization/types/wizards/moneyTransfer';
import Wizard from '../../../modules/wizard/components/Wizard';
import { EWizardName, WIZARD_ALL_ENTITIES } from '../../../modules/wizard/wizardConstants';
import { makeSelectWizardStepName } from '../../../modules/wizard/wizardSelectors';
import { IWizardValidationError, TWizardStepNames } from '../../../modules/wizard/wizardTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { IRouteParams } from '../../../routes/constants/routesConstants';
import { moneyTransferHandleNextAction } from '../moneyTransferActions';
import { EMoneyTransferSteps } from '../moneyTransferConstants';
import {
    makeSelectMoneyTransferGetConflicts,
    makeSelectMoneyTransferPageValid,
    makeSelectMoneyTransferValidateError,
} from '../moneyTransferSelectors';
import MoneyTransferStepAccount from './MoneyTransferStepAccount';
import MoneyTransferStepFinal from './MoneyTransferStepFinal';

interface IMoneyTransferProps {
    basketType: EBasketType;
}

export interface IMatchProps {
    clientId: string;
}

export type TMoneyTransferProps = IMoneyTransferProps & IConnectedProps & IDispatchProps &
    RouteComponentProps<IMatchProps>;

export const MoneyTransfer: FC<TMoneyTransferProps> = (props) => {
    const { clientId } = useParams<IRouteParams>();

    const renderStep = () => {
        switch (props.stepName) {
            case EMoneyTransferSteps.Account:
                return <MoneyTransferStepAccount moneyTransferTranslation={props.moneyTransferTranslation}/>
            case EMoneyTransferSteps.Final:
                return <MoneyTransferStepFinal moneyTransferTranslation={props.moneyTransferTranslation}/>
        }
    }

    return <PageLocale fileNames={[
        TranslationFileNamesEnum.common,
        TranslationFileNamesEnum.corporate,
        TranslationFileNamesEnum.moneyTransfer,
        TranslationFileNamesEnum.wizard,
    ]}>
        <Wizard
            basketType={props.basketType}
            entityId={clientId ? parseInt(clientId) : WIZARD_ALL_ENTITIES}
            isNextDisabled={!props.isPageValid || !!props.conflicts.length}
            name={EWizardName.MONEY_TRANSFER}
            translationProps={{
                error: props.moneyTransferTranslation.error,
                steps: props.moneyTransferTranslation.steps,
                title: props.moneyTransferTranslation.title,
            }}
            validateError={props.validateError}
            onWizardNextHandler={props.handleNext}
        >
            {renderStep()}
        </Wizard>
    </PageLocale>
};

export interface IConnectedProps {
    conflicts: IWizardValidationError[];
    isPageValid: boolean;
    moneyTransferTranslation: IMoneyTransferTranslation;
    stepName: TWizardStepNames;
    validateError: string;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    conflicts: makeSelectMoneyTransferGetConflicts(),
    isPageValid: makeSelectMoneyTransferPageValid(),
    moneyTransferTranslation: makeSelectMoneyTransferTranslation(),
    stepName: makeSelectWizardStepName(),
    validateError: makeSelectMoneyTransferValidateError(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(moneyTransferHandleNextAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(MoneyTransfer);
